<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-users">
      Groups
      <template v-slot:buttons>
        <v-btn color="primary"
               :to="{name: 'admin.group.create'}"
               class="mr-2">
          New Group
        </v-btn>
      </template>
    </page-title>

    <v-row>
      <v-col cols="4">
        <v-form>
          <v-card>
            <v-card-title class="pb-0 font-weight-bold">
              <v-icon left>
                fal fa-fw fa-filter
              </v-icon>
              Filter
            </v-card-title>
            <v-card-text>

              <v-text-field v-model="form.name"
                            type="text"
                            label="Group name"
                            placeholder="Group Name Contains"
                            clearable>
              </v-text-field>

              <v-autocomplete v-model="form.user_id"
                              label="Has member"
                              :items="users"
                              item-value="id"
                              item-text="full_name"
                              clearable>
              </v-autocomplete>


              <v-autocomplete v-model="form.presentation_id"
                              label="With access to presentation"
                              :items="presentations"
                              item-value="id"
                              item-text="name"
                              clearable>
              </v-autocomplete>

              <v-autocomplete v-model="form.share_template_id"
                              label="With access to email template"
                              :items="shareTemplates"
                              item-value="id"
                              item-text="name"
                              clearable>
              </v-autocomplete>

              <v-checkbox v-model="form.showMembers"
                          label="Show members"
                          dense
                          hide-details>
              </v-checkbox>

              <v-checkbox v-model="form.showPresentations"
                          label="Show presentations"
                          dense
                          hide-details>
              </v-checkbox>

              <v-checkbox v-model="form.showShareTemplates"
                          label="Show email templates"
                          dense
                          hide-details>
              </v-checkbox>


            </v-card-text>
            <v-card-actions>
              <v-btn color="primary"
                     @click.prevent="resetForm"
                     block>
                Reset
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="8">

        <v-card class="mb-4">
          <v-toolbar color="primary"
                     dark
                     flat>
            <v-toolbar-title class="font-weight-bold">
              {{ filteredGroups.length | pluralize('Group') }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text class="text-center">
            <strong>Tip:</strong> Put users into a group, then give that group access to presentations and email
            templates.
          </v-card-text>

          <v-card-actions>

            <v-row>
              <v-col cols="12"
                     lg="4">
                <v-btn :to="{name: 'admin.groups.membership_grid'}"
                       block
                       small>
                  Group Membership
                </v-btn>
              </v-col>

              <v-col cols="12"
                     lg="4">
                <v-btn :to="{name: 'admin.groups.presentation_access_grid'}"
                       block
                       small>
                  Presentation Access
                </v-btn>
              </v-col>

              <v-col cols="12"
                     lg="4">
                <v-btn :to="{name: 'admin.groups.share_template_access_grid'}"
                       block
                       small>
                  Email Template Access
                </v-btn>
              </v-col>
            </v-row>

          </v-card-actions>
        </v-card>

        <div v-if="filteredGroups.length">
          <div v-for="group in filteredGroups">

            <v-card class="mb-4">
              <v-card-title class="d-flex justify-space-between">
                <div>
                  <router-link :to="{name: 'admin.group.show', params: {groupID: group.id}}"
                               class="text-decoration-none">
                    {{ group.name }}
                  </router-link>
                </div>
                <div>
                  <v-btn :to="{name: 'admin.group.edit', params: {groupID: group.id}}"
                         x-small>
                    Edit
                  </v-btn>
                  <v-btn @click.prevent="deleteGroup(group)"
                         color="error"
                         :disabled="group.is_all_users"
                         x-small>
                    Delete
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text v-if="form.showMembers || form.showPresentations || form.showShareTemplates">

                <template v-if="form.showMembers">
                  <v-card-subtitle class="py-0 pl-0">
                    Has {{ group.users.length | pluralize('member') }}:
                  </v-card-subtitle>
                  <v-list class="my-0"
                          dense>
                    <template v-for="u in sortedUsers(group)">
                      <v-list-item :key="u.id"
                                   class="pl-0">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div style="display:flex; justify-content: space-between">
                              <div>
                                {{ u.name }}
                              </div>
                            </div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-list>
                </template>

                <template v-if="form.showPresentations">
                  <v-card-subtitle class="py-0 pl-0">
                    Has access to {{ group.presentations.length | pluralize('presentation') }}:
                  </v-card-subtitle>
                  <v-list class="my-0"
                          dense>
                    <template v-for="presentation in sortedPresentations(group)">
                      <v-list-item :key="presentation.id"
                                   class="pl-0">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div style="display:flex; justify-content: space-between">
                              <div>
                                {{ presentation.name }}
                              </div>
                            </div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-list>
                </template>

                <template v-if="form.showShareTemplates">
                  <v-card-subtitle class="py-0 pl-0">
                    Has access to {{ group.templates.length | pluralize('email template') }}:
                  </v-card-subtitle>
                  <v-list class="my-0"
                          dense>
                    <template v-for="template in sortedShareTemplates(group)">
                      <v-list-item :key="template.id"
                                   class="pl-0">
                        <v-list-item-content>
                          <v-list-item-title>
                            <div style="display:flex; justify-content: space-between">
                              <div>
                                {{ template.name }}
                              </div>
                            </div>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-list>
                </template>
              </v-card-text>
            </v-card>

          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {find, orderBy} from "lodash";
import PageTitle from '../../components/PageTitle.vue';
import groupService from '../../services/groupService';

export default {
  components: {PageTitle},
  metaInfo() {
    this.$store.commit('setTitle', 'Groups');
    return {
      title: this.$store.getters.title,
    }
  },
  data() {
    return {
      breadcrumbs: [
        {text: 'Groups', disabled: false, exact: true, to: {name: 'admin.groups.index'}},
        {text: 'List'}
      ],
      form: new Form({
        showMembers: false,
        showPresentations: false,
        showShareTemplates: false,
        name: '',
        user_id: '',
        presentation_id: '',
        share_template_id: '',
      }),
      loading: false,
    }
  },
  computed: {
    ...mapGetters([
      'groups',
      'presentations',
      'team',
      'users',
      'shareTemplates',
    ]),
    filter: function () {
      if (this.form.name?.length === 0 && !this.form.presentation_id && !this.form.user_id) {
        return false;
      }
      return {
        name: this.form.name?.toLowerCase().trim(),
        presentation_id: (this.form.presentation_id) ? parseInt(this.form.presentation_id) : null,
        user_id: (this.form.user_id) ? parseInt(this.form.user_id) : null,
        template_id: (this.form.share_template_id) ? parseInt(this.form.share_template_id) : null,
      };
    },
    filteredGroups: function () {
      const filter = this.filter;
      if (!filter) {
        return this.groups;
      }
      return this.groups.filter(g => {
        if (filter.name && g.name.toLowerCase().indexOf(filter.name) === -1) {
          return false;
        }
        if (filter.user_id) {
          let u = find(g.users, u => u.id === filter.user_id);
          if (!u) {
            return false;
          }
        }
        if (filter.presentation_id) {
          let p = find(g.presentations, p => p.id === filter.presentation_id);
          if (!p) {
            return false;
          }
        }
        if (filter.share_template_id) {
          let t = find(g.templates, t => t.id === filter.share_template_id);
          if (!t) {
            return false;
          }
        }
        return true;
      });
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      //await this.$store.dispatch('fetchGroups');
      this.loading = false;
    },
    resetForm() {
      this.form.showMembers = false;
      this.form.showPresentations = false;
      this.form.showShareTemplates = false;
      this.form.name = '';
      this.form.presentation_id = '';
      this.form.share_template_id = '';
      this.form.user_id = '';
    },

    sortedPresentations(group) {
      return orderBy(group.presentations, ['slug', 'id'], ['asc', 'asc']);
    },
    sortedUsers(group) {
      return orderBy(group.users, ['slug', 'id'], ['asc', 'asc']);
    },
    sortedShareTemplates(group) {
      return orderBy(group.templates, ['slug', 'id'], ['asc', 'asc']);
    },
    async deleteGroup(group) {
      if (group.is_all_users) {
        this.$toast.error('You cannot delete this group.');
        return;
      }
      if (confirm('Are you sure you want to delete this group?')) {
        try {
          await groupService.deleteGroup(group.id)
          await this.$store.dispatch('fetchGroups');
        } catch (error) {
          this.$toast.error('There was an error deleting that group.');
        }
      }
    }
  }
}
</script>
